/**
 * @fileoverview Vue composable for Particle Globe visualization
 * Implements land-based particle generation using canvas rasterization
 * and shader-based animation for "joining" effect.
 * 
 * UPDATED: Uses GPGPU for ambient field particles.
 * UPDATED: Implements text morphing for experience section.
 */
/**
 * Particle Globe Composable - 3D Globe with Particle Morphing
 * Trigger deployment: v1.0.0
 */
import { ref } from 'vue';
import * as THREE from 'three';
import { GPUComputationRenderer } from 'three/examples/jsm/misc/GPUComputationRenderer.js';
import { useAppStore } from '@/stores/app';

/**
 * Particle Globe composable
 * @param {Object} canvasRef - Canvas element ref
 * @param {Object} config - Globe configuration
 * @returns {Object} Globe controls and state
 */
export function useParticleGlobe(canvasRef, config) {
    const store = useAppStore();

    // Three.js objects
    let scene, camera, renderer, globe, fieldParticles, cityMarkers, logoParticles, animationId;

    // GPGPU
    let gpuCompute;
    let velocityVariable, positionVariable;
    let positionUniforms, velocityUniforms;

    // Office locations (lat, lon)
    const officeLocations = [
        { name: 'London', lat: 51.5074, lon: -0.1278 },
        { name: 'New York', lat: 40.7128, lon: -74.0060 },
        { name: 'Los Angeles', lat: 34.0522, lon: -118.2437 }
    ];

    // State
    const selectedManager = ref(null);
    let startTime = 0;

    // Mouse/drag state
    let isDragging = false;
    let previousMousePosition = { x: 0, y: 0 };
    let targetRotationY = 0;
    const baseRotationVelocity = 0.002;
    let lastTime = 0;

    // Mouse position tracking for particle avoidance
    let mouse2D = new THREE.Vector2(-999, -999); // Normalized device coordinates
    let mouse3D = new THREE.Vector3(0, 0, 0); // World space position
    let raycaster = new THREE.Raycaster();

    // Zoom state
    let isZoomed = false;
    let targetCameraZ = 2.5;
    let targetGlobeRotationY = 0;
    let focusCity = null;

    // Experience State
    let isExperienceActive = false;
    let isLetsTalkActive = false;
    let morphProgress = 0;
    let boothMorphProgress = 0;

    // Constants
    const WIDTH = 32; // 32x32 = 1024 particles (Field)
    const PARTICLES = WIDTH * WIDTH;

    /**
     * Initialize Three.js scene
     */
    async function initScene() {
        if (!canvasRef.value) return;

        const canvas = canvasRef.value;

        // Scene setup
        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        camera.position.z = 2.5;

        // Renderer setup
        renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: !config.isLowEnd,
            alpha: true
        });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, config.isLowEnd ? 1 : 2));

        // --- PARTICLE GENERATION ---

        // 1. Fetch and Rasterize Country Data
        try {
            const response = await fetch('https://unpkg.com/world-atlas@2.0.2/countries-110m.json');
            const topology = await response.json();
            const { feature } = await import('topojson-client');
            const countries = feature(topology, topology.objects.countries);

            // Create offscreen canvas for rasterization
            const mapWidth = 2048;
            const mapHeight = 1024;
            const mapCanvas = document.createElement('canvas');
            mapCanvas.width = mapWidth;
            mapCanvas.height = mapHeight;
            const ctx = mapCanvas.getContext('2d');

            // Fill background black (ocean)
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, mapWidth, mapHeight);

            // Draw countries white
            ctx.fillStyle = '#FFFFFF';

            const project = (lon, lat) => {
                const x = (lon + 180) * (mapWidth / 360);
                const y = (90 - lat) * (mapHeight / 180);
                return [x, y];
            };

            countries.features.forEach(country => {
                const drawPolygon = (rings) => {
                    ctx.beginPath();
                    rings.forEach((ring, idx) => {
                        ring.forEach((coord, i) => {
                            const [x, y] = project(coord[0], coord[1]);
                            if (i === 0) ctx.moveTo(x, y);
                            else ctx.lineTo(x, y);
                        });
                        if (idx === 0) ctx.closePath(); // Outer ring
                    });
                    ctx.fill();
                };

                if (country.geometry.type === 'Polygon') {
                    drawPolygon(country.geometry.coordinates);
                } else if (country.geometry.type === 'MultiPolygon') {
                    country.geometry.coordinates.forEach(drawPolygon);
                }
            });

            // 2. Sample points from canvas
            const imageData = ctx.getImageData(0, 0, mapWidth, mapHeight);
            const data = imageData.data;
            const targetPositions = [];
            const startPositions = [];
            const randomOffsets = [];

            // Density control: skip pixels to manage particle count
            // Higher step = fewer particles
            const step = config.isLowEnd ? 4 : 2;

            // Reduce particle count by 20% using random skip
            const skipProbability = 0.20;

            for (let y = 0; y < mapHeight; y += step) {
                for (let x = 0; x < mapWidth; x += step) {
                    const i = (y * mapWidth + x) * 4;
                    // Check red channel (since we drew white on black)
                    if (data[i] > 128) {
                        // Skip 20% of particles randomly
                        if (Math.random() < skipProbability) continue;
                        // This is land
                        // Map back to 3D coordinates
                        const lon = (x / mapWidth) * 360 - 180;
                        const lat = 90 - (y / mapHeight) * 180;

                        // Convert to Cartesian
                        const phi = (90 - lat) * (Math.PI / 180);
                        const theta = (lon + 180) * (Math.PI / 180);
                        const r = 1.0;

                        const tx = -(r * Math.sin(phi) * Math.cos(theta));
                        const tz = (r * Math.sin(phi) * Math.sin(theta));
                        const ty = (r * Math.cos(phi));

                        targetPositions.push(tx, ty, tz);

                        // Start position: Random point in a larger sphere
                        // "Scene full of particles" - much wider spread
                        const startR = 5.0 + Math.random() * 15.0;
                        const startPhi = Math.acos(2 * Math.random() - 1);
                        const startTheta = Math.random() * 2 * Math.PI;

                        const sx = startR * Math.sin(startPhi) * Math.cos(startTheta);
                        const sy = startR * Math.sin(startPhi) * Math.sin(startTheta);
                        const sz = startR * Math.cos(startPhi);

                        startPositions.push(sx, sy, sz);

                        // Random offset for "drift" animation
                        randomOffsets.push(Math.random(), Math.random(), Math.random());
                    }
                }
            }

            // Generate Telephone Booth positions and colors
            const boothPositions = [];
            const boothColors = [];
            const numParticles = targetPositions.length / 3;

            // Booth dimensions (relative to globe radius of ~1.0)
            const bw = 0.6;  // width
            const bh = 1.3;  // height (taller for roof)
            const bd = 0.6;  // depth

            // Structural metrics
            const baseH = 0.1; // Solid base height
            const signH = 0.1; // Sign panel height
            const roofH = 0.15; // Roof height
            const windowH = bh - baseH - signH - roofH; // Remaining height for windows

            for (let i = 0; i < numParticles; i++) {
                // Distribute particles across booth volume
                const u = Math.random();
                const v = Math.random();
                const w = Math.random();

                // Map to booth coordinates (centered at origin)
                let bx = (u - 0.5) * bw;
                let by = v * bh - bh / 2;
                let bz = (w - 0.5) * bd;

                let rCol = 0.808; // #CE1126 (London Red)
                let gCol = 0.067;
                let bCol = 0.149;

                // Determine part of booth
                const yFromBottom = by + bh / 2;

                // 1. Base (Solid Red)
                if (yFromBottom < baseH) {
                    // Keep red
                }
                // 2. Roof (Domed)
                else if (yFromBottom > bh - roofH) {
                    // Domed shape logic
                    const roofY = yFromBottom - (bh - roofH); // 0 to roofH
                    const normRoofY = roofY / roofH;

                    // Taper inward for dome
                    const domeScale = Math.cos(normRoofY * 1.0); // Curve in
                    if (Math.abs(bx) > bw / 2 * domeScale || Math.abs(bz) > bd / 2 * domeScale) {
                        // Outside dome, push in
                        bx *= domeScale;
                        bz *= domeScale;
                    }

                    // Cornice detail at bottom of roof
                    if (normRoofY < 0.2) {
                        bx *= 1.1; // Flared out
                        bz *= 1.1;
                    }
                }
                // 3. Sign Panel (White with Red Frame)
                else if (yFromBottom > bh - roofH - signH) {
                    // Check if on face
                    const isFaceX = Math.abs(Math.abs(bx) - bw / 2) < 0.05;
                    const isFaceZ = Math.abs(Math.abs(bz) - bd / 2) < 0.05;

                    if (isFaceX || isFaceZ) {
                        // Inset slightly for sign face
                        if (Math.abs(bx) < bw / 2 * 0.9 && Math.abs(bz) < bd / 2 * 0.9) {
                            // White background for sign
                            rCol = 1.0; gCol = 1.0; bCol = 1.0;

                            // "TELEPHONE" text approximation (Blocky letters)
                            // Map x/z to 9 distinct letter slots
                            let pos = 0;
                            if (isFaceZ) pos = (bx + bw / 2) / bw; // 0 to 1
                            else if (isFaceX) pos = (bz + bd / 2) / bd;

                            // 9 letters, with padding
                            const numLetters = 9;
                            const letterWidth = 1.0 / numLetters;
                            const letterIndex = Math.floor(pos * numLetters);
                            const letterLocal = (pos * numLetters) % 1.0;

                            // Draw "text" if within the central part of the letter slot
                            // and within the vertical band
                            const vertCenter = Math.abs(by - (bh / 2 - roofH - signH / 2)) < 0.025;
                            const horizCenter = letterLocal > 0.2 && letterLocal < 0.8;

                            if (vertCenter && horizCenter) {
                                // Make it black
                                rCol = 0.1; gCol = 0.1; bCol = 0.1;
                            }
                        }
                    }
                }
                // 4. Windows (3x6 Grid)
                else {
                    // Check if on outer shell
                    const isFaceX = Math.abs(Math.abs(bx) - bw / 2) < 0.05;
                    const isFaceZ = Math.abs(Math.abs(bz) - bd / 2) < 0.05;

                    if (isFaceX || isFaceZ) {
                        // Window Grid Logic
                        // 3 columns, 6 rows
                        const cols = 3;
                        const rows = 6;

                        // Normalize coordinates within window area
                        let nx = (bx + bw / 2) / bw; // 0 to 1
                        let nz = (bz + bd / 2) / bd; // 0 to 1
                        let ny = (yFromBottom - baseH) / windowH; // 0 to 1

                        // Frame thickness
                        const frameT = 0.08; // Relative thickness

                        let isWindow = false;

                        if (isFaceZ) { // Front/Back
                            const colX = Math.floor(nx * cols);
                            const rowY = Math.floor(ny * rows);
                            const inCol = (nx * cols) % 1 > frameT && (nx * cols) % 1 < (1 - frameT);
                            const inRow = (ny * rows) % 1 > frameT / 2 && (ny * rows) % 1 < (1 - frameT / 2);
                            if (inCol && inRow) isWindow = true;
                        } else if (isFaceX) { // Sides
                            const colZ = Math.floor(nz * cols);
                            const rowY = Math.floor(ny * rows);
                            const inCol = (nz * cols) % 1 > frameT && (nz * cols) % 1 < (1 - frameT);
                            const inRow = (ny * rows) % 1 > frameT / 2 && (ny * rows) % 1 < (1 - frameT / 2);
                            if (inCol && inRow) isWindow = true;
                        }

                        if (isWindow) {
                            // Blue Glass
                            rCol = 0.529; // #87CEEB (Sky Blue)
                            gCol = 0.808;
                            bCol = 0.922;

                            // Make glass slightly transparent/darker in shader? 
                            // For now just use color.

                            // Push in slightly
                            if (isFaceZ) bz *= 0.9;
                            if (isFaceX) bx *= 0.9;
                        }
                    } else {
                        // Interior particles - hide or make dark
                        rCol *= 0.5; gCol *= 0.5; bCol *= 0.5;
                    }
                }

                boothPositions.push(bx, by, bz);
                boothColors.push(rCol, gCol, bCol);
            }

            // 3. Create Geometry and Shader Material

            // Generate Text Positions
            const textPositions = createTextPoints(targetPositions.length / 3);

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(startPositions, 3));
            geometry.setAttribute('targetPosition', new THREE.Float32BufferAttribute(targetPositions, 3));
            geometry.setAttribute('textPosition', new THREE.Float32BufferAttribute(textPositions, 3));
            geometry.setAttribute('boothPosition', new THREE.Float32BufferAttribute(boothPositions, 3));
            geometry.setAttribute('boothColor', new THREE.Float32BufferAttribute(boothColors, 3));
            geometry.setAttribute('randomOffset', new THREE.Float32BufferAttribute(randomOffsets, 3));

            const material = new THREE.ShaderMaterial({
                uniforms: {
                    uTime: { value: 0 },
                    uProgress: { value: 0 }, // 0 = start, 1 = target
                    uMorph: { value: 0 }, // 0 = globe, 1 = text
                    uMorphBooth: { value: 0 }, // 0 = globe/text, 1 = booth
                    uColor: { value: new THREE.Color(0xF77E2D) }
                },
                vertexShader: `
                    uniform float uTime;
                    uniform float uProgress;
                    uniform float uMorph;
                    uniform float uMorphBooth;
                    
                    attribute vec3 targetPosition;
                    attribute vec3 textPosition;
                    attribute vec3 boothPosition;
                    attribute vec3 boothColor;
                    attribute vec3 randomOffset;
                    
                    varying float vAlpha;
                    varying vec3 vColor;

                    void main() {
                        // 1. Globe Formation (Start -> Globe)
                        float t = smoothstep(0.0, 1.0, uProgress);
                        vec3 globePos = mix(position, targetPosition, t);
                        
                        // Add subtle drift noise to globe
                        float drift = 0.02 * t;
                        globePos.x += sin(uTime * 0.5 + randomOffset.x * 10.0) * drift;
                        globePos.y += cos(uTime * 0.3 + randomOffset.y * 10.0) * drift;
                        globePos.z += sin(uTime * 0.7 + randomOffset.z * 10.0) * drift;

                        // 2. Text Morph (Globe -> Text)
                        float m = uMorph * uMorph * (3.0 - 2.0 * uMorph);
                        
                        vec3 noise = vec3(
                            sin(uTime * 2.0 + randomOffset.x * 20.0),
                            cos(uTime * 2.0 + randomOffset.y * 20.0),
                            sin(uTime * 2.0 + randomOffset.z * 20.0)
                        ) * 0.5 * sin(m * 3.14159);

                        vec3 textPos = mix(globePos, textPosition, m) + noise;

                        // 3. Booth Morph (Globe -> Booth)
                        float b = uMorphBooth * uMorphBooth * (3.0 - 2.0 * uMorphBooth);
                        vec3 boothPos = mix(globePos, boothPosition, b);
                        
                        // Final position: choose between text or booth (booth takes priority)
                        vec3 finalPos = mix(textPos, boothPos, b);
                        
                        // Jiggle for text
                        float jiggle = 0.015 * m * (1.0 - b);
                        finalPos.x += sin(uTime * 3.0 + randomOffset.x * 10.0) * jiggle;
                        finalPos.y += cos(uTime * 2.5 + randomOffset.y * 10.0) * jiggle;
                        finalPos.z += sin(uTime * 3.5 + randomOffset.z * 10.0) * jiggle;
                        
                        // Visibility Logic
                        if (uMorph > 0.0 && length(textPosition) < 0.01) {
                            vAlpha = 1.0 - m; // Fade out unused text particles
                        } else {
                            vAlpha = 0.8;
                        }
                        
                        // Color Logic: Mix globe color with booth color
                        vec3 baseColor = vec3(0.969, 0.494, 0.176); // Globe orange
                        vColor = mix(baseColor, boothColor, b);

                        vec4 mvPosition = modelViewMatrix * vec4(finalPos, 1.0);
                        gl_Position = projectionMatrix * mvPosition;
                        
                        gl_PointSize = 4.0 * (1.0 / -mvPosition.z);
                    }
                `,
                fragmentShader: `
                    uniform vec3 uColor;
                    varying float vAlpha;
                    varying vec3 vColor;
                    
                    void main() {
                        // Circular particle
                        vec2 coord = gl_PointCoord - vec2(0.5);
                        if (length(coord) > 0.5) discard;
                        
                        gl_FragColor = vec4(vColor, vAlpha);
                    }
                `,
                transparent: true,
                blending: THREE.NormalBlending,
                depthWrite: false
            });

            globe = new THREE.Points(geometry, material);
            scene.add(globe);

            // Tilt globe to make North America and Europe more visible
            globe.rotation.x = 22.5 * (Math.PI / 180); // 22.5 degrees tilt
            globe.rotation.y = -Math.PI / 2; // Center roughly on map center
            targetRotationY = globe.rotation.y;

            console.log(`✓ Particle Globe initialized with ${targetPositions.length / 3} particles`);

        } catch (error) {
            console.error('Failed to load country data:', error);
        }

        // Lighting (optional for points but good practice)
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);

        // Create field particles (GPGPU)
        initGPGPU();
        createFieldParticles();

        // Create city markers
        createCityMarkers();

        // Create logo particles
        createLogoParticles();
    }

    /**
     * Create points from text rasterization
     */
    function createTextPoints(count) {
        const canvas = document.createElement('canvas');
        canvas.width = 2048;
        canvas.height = 1024;
        const ctx = canvas.getContext('2d');

        // Clear to black
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'right'; // Right align to center
        ctx.textBaseline = 'middle';

        // Constraints:
        // Globe Radius is 1.0.
        // Canvas maps to World Width 8.0 (-4 to 4) and Height 4.0 (-2 to 2).
        // To fit in Globe (X: -1 to 1, Y: -1 to 1):
        // X range on canvas: 768 to 1280 (Width 512)
        // Y range on canvas: 256 to 768 (Height 512)

        // We want right alignment ending at the center of the canvas (X = 1024)
        // We want to fit 4 items vertically within Y = -0.8 to 0.8 (Canvas Y ~ 300 to 720)

        const startX = 1024; // Center of canvas
        const startY = 250;
        const gapY = 180;

        const stats = [
            { val: "$3.9B" },
            { val: "80%" },
            { val: "75%" },
            { val: ">500" }
        ];

        stats.forEach((stat, i) => {
            const y = startY + i * gapY;

            // Larger font again, as we have side-by-side layout
            ctx.font = 'bold 120px Inter, sans-serif';
            ctx.fillText(stat.val, startX, y);
        });

        // Sample points
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const points = [];

        // Adaptive step
        let pixelCount = 0;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i] > 128) pixelCount++;
        }

        const step = Math.max(1, Math.floor(Math.sqrt(pixelCount / count)));

        for (let y = 0; y < canvas.height; y += step) {
            for (let x = 0; x < canvas.width; x += step) {
                const i = (y * canvas.width + x) * 4;
                if (data[i] > 128) {
                    // Map to 3D
                    // Canvas 0..2048 -> -4..4
                    const px = (x / canvas.width - 0.5) * 8.0;
                    const py = -(y / canvas.height - 0.5) * 4.0; // Flip Y
                    const pz = 0;

                    points.push(px, py, pz);
                }
            }
        }

        // Fill or trim
        const result = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            if (i < points.length / 3) {
                result[i3] = points[i3];
                result[i3 + 1] = points[i3 + 1];
                result[i3 + 2] = points[i3 + 2];
            } else {
                result[i3] = 0;
                result[i3 + 1] = 0;
                result[i3 + 2] = 0;
            }
        }

        // Shuffle
        for (let i = count - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const tempX = result[i * 3];
            const tempY = result[i * 3 + 1];
            const tempZ = result[i * 3 + 2];

            result[i * 3] = result[j * 3];
            result[i * 3 + 1] = result[j * 3 + 1];
            result[i * 3 + 2] = result[j * 3 + 2];
            result[j * 3] = tempX;
            result[j * 3 + 1] = tempY;
            result[j * 3 + 2] = tempZ;
        }

        return result;
    }

    /**
     * Initialize GPGPU Computation
     */
    function initGPGPU() {
        gpuCompute = new GPUComputationRenderer(WIDTH, WIDTH, renderer);

        if (renderer.capabilities.isWebGL2 === false) {
            gpuCompute.setDataType(THREE.HalfFloatType);
        }

        const dtPosition = gpuCompute.createTexture();
        const dtVelocity = gpuCompute.createTexture();

        initTexture(dtPosition, dtVelocity);

        velocityVariable = gpuCompute.addVariable("textureVelocity", `
            uniform float uTime;
            uniform vec3 uMouse;
            uniform float uGravity;
            uniform float uRepulsionRadius;
            uniform float uRepulsionStrength;
            uniform float uDeltaTime;

            void main() {
                vec2 uv = gl_FragCoord.xy / resolution.xy;
                vec3 pos = texture2D(texturePosition, uv).xyz;
                vec3 vel = texture2D(textureVelocity, uv).xyz;
                float alpha = texture2D(texturePosition, uv).w; // Stored in w component of position

                // Physics constants
                float drag = 0.995;
                float globeRadius = 1.0;

                // 1. Gravity (pull towards 0,0,0)
                float dist = length(pos);
                if (dist > 0.01) {
                    float force = -uGravity / (dist * dist);
                    vel += normalize(pos) * force * uDeltaTime;
                }

                // 2. Mouse Repulsion
                if (length(uMouse) > 0.0) {
                    vec3 diff = pos - uMouse;
                    float distSq = dot(diff, diff);
                    if (distSq < uRepulsionRadius * uRepulsionRadius) {
                        float dist = sqrt(distSq);
                        float force = (uRepulsionRadius - dist) * uRepulsionStrength;
                        vel += (diff / dist) * force * uDeltaTime;
                    }
                }

                // 3. Drag
                vel *= drag;

                gl_FragColor = vec4(vel, 1.0);
            }
        `, dtVelocity);

        positionVariable = gpuCompute.addVariable("texturePosition", `
            uniform float uDeltaTime;
            uniform float uTime;

            // Helper for random
            float rand(vec2 co){
                return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
            }

            void main() {
                vec2 uv = gl_FragCoord.xy / resolution.xy;
                vec3 pos = texture2D(texturePosition, uv).xyz;
                vec3 vel = texture2D(textureVelocity, uv).xyz;
                float alpha = texture2D(texturePosition, uv).w;

                // Apply velocity
                pos += vel * uDeltaTime * 20.0; // Scale up for visibility

                // Check boundaries / Respawn
                float dist = length(pos);
                
                // If hit globe or too far
                if (dist <= 1.0 || dist > 6.0) {
                    if (uTime < 5.0) {
                        // Respawn
                        float seed = uTime + uv.x * 10.0;
                        float r = 2.5 + rand(vec2(seed, uv.y)) * 2.0;
                        float theta = rand(vec2(seed + 1.0, uv.y)) * 6.28318;
                        float phi = acos(2.0 * rand(vec2(seed + 2.0, uv.y)) - 1.0);

                        pos.x = r * sin(phi) * cos(theta);
                        pos.y = r * sin(phi) * sin(theta);
                        pos.z = r * cos(phi);
                        
                        // Reset alpha
                        alpha = 0.0; 
                    } else {
                        // Stop respawning, just hide
                        alpha = 0.0;
                    }
                } else {
                    // Fade in
                    alpha = min(1.0, alpha + uDeltaTime);
                }

                gl_FragColor = vec4(pos, alpha);
            }
        `, dtPosition);

        gpuCompute.setVariableDependencies(velocityVariable, [positionVariable, velocityVariable]);
        gpuCompute.setVariableDependencies(positionVariable, [positionVariable, velocityVariable]);

        velocityUniforms = velocityVariable.material.uniforms;
        positionUniforms = positionVariable.material.uniforms;

        velocityUniforms.uTime = { value: 0.0 };
        velocityUniforms.uMouse = { value: new THREE.Vector3(0, 0, 0) };
        velocityUniforms.uGravity = { value: 0.05 };
        velocityUniforms.uRepulsionRadius = { value: 1.2 };
        velocityUniforms.uRepulsionStrength = { value: 1.5 };
        velocityUniforms.uDeltaTime = { value: 0.0 };

        positionUniforms.uDeltaTime = { value: 0.0 };
        positionUniforms.uTime = { value: 0.0 };

        const error = gpuCompute.init();
        if (error !== null) {
            console.error(error);
        }
    }

    function initTexture(texturePosition, textureVelocity) {
        const posArray = texturePosition.image.data;
        const velArray = textureVelocity.image.data;

        for (let k = 0, kl = posArray.length; k < kl; k += 4) {
            // Random position in spherical shell
            const r = 1.5 + Math.random() * 2.5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);

            posArray[k + 0] = r * Math.sin(phi) * Math.cos(theta);
            posArray[k + 1] = r * Math.sin(phi) * Math.sin(theta);
            posArray[k + 2] = r * Math.cos(phi);
            posArray[k + 3] = 1.0; // Alpha

            // Random velocity
            velArray[k + 0] = (Math.random() - 0.5) * 0.01;
            velArray[k + 1] = (Math.random() - 0.5) * 0.01;
            velArray[k + 2] = (Math.random() - 0.5) * 0.01;
            velArray[k + 3] = 0; // Unused
        }
    }

    /**
     * Create city markers for office locations
     */
    function createCityMarkers() {
        cityMarkers = new THREE.Group();

        officeLocations.forEach(location => {
            // Convert lat/lon to 3D position
            const phi = (90 - location.lat) * (Math.PI / 180);
            const theta = (location.lon + 180) * (Math.PI / 180);
            const radius = 1.06; // Slightly closer than before

            const x = -(radius * Math.sin(phi) * Math.cos(theta));
            const z = (radius * Math.sin(phi) * Math.sin(theta));
            const y = (radius * Math.cos(phi));

            // Create sprite for label
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = 512;
            canvas.height = 128;

            // Enable better text rendering
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';

            // Pill dimensions
            const cardWidth = 400;
            const cardHeight = 90;
            const startX = (canvas.width - cardWidth) / 2;
            const startY = (canvas.height - cardHeight) / 2;
            const borderRadius = 45; // Full pill

            // Glassy Pill Background
            // Lighter background to contrast with black text
            const gradient = context.createLinearGradient(startX, startY, startX, startY + cardHeight);
            gradient.addColorStop(0, 'rgba(255, 255, 255, 0.85)');
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0.65)');

            context.fillStyle = gradient;

            // Add shadow for depth
            context.shadowColor = 'rgba(0, 0, 0, 0.2)';
            context.shadowBlur = 10;
            context.shadowOffsetY = 4;

            roundRect(context, startX, startY, cardWidth, cardHeight, borderRadius);
            context.fill();

            // Reset shadow
            context.shadowColor = 'transparent';

            // Border
            context.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            context.lineWidth = 2;
            roundRect(context, startX, startY, cardWidth, cardHeight, borderRadius);
            context.stroke();

            // Text
            context.fillStyle = '#000000'; // Black text
            context.font = '700 50px Inter, -apple-system, sans-serif';
            context.textAlign = 'center';
            context.textBaseline = 'middle';

            context.fillText(location.name.toUpperCase(), canvas.width / 2, canvas.height / 2 + 2);

            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;

            const spriteMaterial = new THREE.SpriteMaterial({
                map: texture,
                transparent: true,
                opacity: 1,
                depthWrite: false
            });

            const sprite = new THREE.Sprite(spriteMaterial);
            sprite.position.set(x, y, z);
            sprite.scale.set(0.35, 0.0875, 1); // Adjust scale for new dimensions

            cityMarkers.add(sprite);
        });

        scene.add(cityMarkers);
        console.log(`✓ City markers initialized for ${officeLocations.length} locations`);
    }

    /**
     * Helper function to draw rounded rectangles
     */
    function roundRect(ctx, x, y, width, height, radius) {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    }

    /**
     * Create ambient field particles that float around the globe
     */
    function createFieldParticles() {
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(PARTICLES * 3);
        const references = new Float32Array(PARTICLES * 2);

        for (let i = 0; i < PARTICLES; i++) {
            const i3 = i * 3;
            positions[i3] = 0;
            positions[i3 + 1] = 0;
            positions[i3 + 2] = 0;

            const xx = (i % WIDTH) / WIDTH;
            const yy = Math.floor(i / WIDTH) / WIDTH;

            references[i * 2] = xx;
            references[i * 2 + 1] = yy;
        }

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('reference', new THREE.BufferAttribute(references, 2));

        const material = new THREE.ShaderMaterial({
            uniforms: {
                uColor: { value: new THREE.Color(0xF77E2D) },
                texturePosition: { value: null }
            },
            vertexShader: `
                uniform vec3 uColor;
                uniform sampler2D texturePosition;
                attribute vec2 reference;
                varying float vAlpha;

                void main() {
                    vec4 pos = texture2D(texturePosition, reference);
                    vec3 currentPos = pos.xyz;
                    vAlpha = pos.w;

                    vec4 mvPosition = modelViewMatrix * vec4(currentPos, 1.0);
                    gl_Position = projectionMatrix * mvPosition;
                    
                    // Size attenuation
                    gl_PointSize = 6.0 * (1.0 / -mvPosition.z);
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                varying float vAlpha;
                
                void main() {
                    vec2 coord = gl_PointCoord - vec2(0.5);
                    if (length(coord) > 0.5) discard;
                    gl_FragColor = vec4(uColor, vAlpha);
                }
            `,
            transparent: true,
            blending: THREE.NormalBlending,
            depthWrite: false
        });

        fieldParticles = new THREE.Points(geometry, material);
        fieldParticles.frustumCulled = false; // Important for GPGPU
        scene.add(fieldParticles);

        console.log(`✓ Field particles initialized with ${PARTICLES} particles (GPGPU)`);
    }

    /**
     * Create static logo particles for "&capital"
     */
    /**
     * Create static logo particles for "&capital"
     */
    function createLogoParticles() {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 1024;
        canvas.height = 256;

        // Draw text with color coding for types
        // Red (255,0,0) = "&" (Type 0)
        // Green (0,255,0) = "capital" (Type 1)
        // Blue (0,0,255) = "Cities" (Type 2)

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // 1. Draw "&" (Red)
        ctx.font = 'bold 180px Inter, sans-serif';
        ctx.fillStyle = '#FF0000';
        // Position "&" slightly to the left of center
        ctx.fillText('&', canvas.width / 2 - 280, canvas.height / 2 - 20);

        // 2. Draw "capital" (Green)
        ctx.fillStyle = '#00FF00';
        // Position "capital" to the right of "&"
        ctx.fillText('capital', canvas.width / 2 + 50, canvas.height / 2 - 20);

        // 3. Draw Cities (Blue)
        ctx.font = '600 48px Inter, sans-serif'; // Smaller font for cities
        ctx.fillStyle = '#0000FF';
        ctx.fillText('London | New York | Los Angeles', canvas.width / 2, canvas.height / 2 + 100);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const points = [];
        const types = [];

        // Sample points
        const step = 2; // Density
        for (let y = 0; y < canvas.height; y += step) {
            for (let x = 0; x < canvas.width; x += step) {
                const i = (y * canvas.width + x) * 4;

                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const a = data[i + 3];

                if (a > 128) {
                    // Determine type based on color
                    let type = 0;
                    if (r > 128) type = 0;      // &
                    else if (g > 128) type = 1; // capital
                    else if (b > 128) type = 2; // Cities

                    // Map to 3D position
                    const nx = (x / canvas.width) - 0.5;
                    const ny = (y / canvas.height) - 0.5;

                    // Scale
                    const scale = 1.0;
                    const px = nx * scale;
                    const py = -ny * (scale * (canvas.height / canvas.width)) - 1.25;
                    const pz = 0.5;

                    points.push(px, py, pz);
                    types.push(type);

                    // Add random offset for jiggle phase AND explosion direction
                    points.push(Math.random(), Math.random(), Math.random());
                }
            }
        }

        const geometry = new THREE.BufferGeometry();
        const positionAttribute = new THREE.Float32BufferAttribute(points.filter((_, i) => i % 6 < 3), 3);
        const randomAttribute = new THREE.Float32BufferAttribute(points.filter((_, i) => i % 6 >= 3), 3);
        const typeAttribute = new THREE.Float32BufferAttribute(types, 1);

        geometry.setAttribute('position', positionAttribute);
        geometry.setAttribute('aRandom', randomAttribute);
        geometry.setAttribute('aType', typeAttribute);

        const material = new THREE.ShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uProgress: { value: 0 },
                uKick: { value: 0 },
                uExplode: { value: 0 }, // Controls disintegration (0 to 1)
                uReform: { value: 0 },  // Controls reformation (0 to 1)
                uColor: { value: new THREE.Color(0x000000) },
                uDarkMode: { value: 0.0 } // 0.0 = light mode, 1.0 = dark mode
            },
            vertexShader: `
                uniform float uTime;
                uniform float uProgress;
                uniform float uKick;
                uniform float uExplode;
                uniform float uReform;
                attribute vec3 aRandom;
                attribute float aType; // 0=&, 1=capital, 2=cities
                varying float vType; // Pass type to fragment shader
                
                // Simplex noise for shake
                vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x, 289.0); }
                float snoise(vec2 v){
                  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                           -0.577350269189626, 0.024390243902439);
                  vec2 i  = floor(v + dot(v, C.yy) );
                  vec2 x0 = v - i + dot(i, C.xx);
                  vec2 i1;
                  i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
                  vec4 x12 = x0.xyxy + C.xxzz;
                  x12.xy -= i1;
                  i = mod(i, 289.0);
                  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
                  + i.x + vec3(0.0, i1.x, 1.0 ));
                  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
                  m = m*m ;
                  m = m*m ;
                  vec3 x = 2.0 * fract(p * C.www) - 1.0;
                  vec3 h = abs(x) - 0.5;
                  vec3 ox = floor(x + 0.5);
                  vec3 a0 = x - ox;
                  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
                  vec3 g;
                  g.x  = a0.x  * x0.x  + h.x  * x0.y;
                  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
                  return 130.0 * dot(m, g);
                  }

                void main() {
                    vec3 targetPos = position;
                    
                    // --- 1. Formation ---
                    // Start position: Random cloud
                    vec3 startPos = (aRandom - 0.5) * 20.0;
                    startPos.z += 5.0; 
                    
                    float t = smoothstep(0.0, 1.0, uProgress);
                    vec3 pos = mix(startPos, targetPos, t);
                    
                    // Hide Cities initially (Type 2)
                    // They should only appear when the explosion starts
                    if (aType > 1.5 && uExplode < 0.01 && uReform < 0.01) {
                         // Move them behind camera or scale to 0
                         gl_PointSize = 0.0;
                         // Also move them away to be safe
                         pos = vec3(0.0, 0.0, 100.0);
                    }

                    // --- 2. Kick Animation ---
                    // Only applies if formed (t=1)
                    if (t > 0.9) {
                        float kick = smoothstep(0.0, 1.0, uKick);
                        
                        if (aType < 0.5) { 
                            // Type 0 (&): Rotate and kick
                            // Pivot point for "&" (approximate center in world space)
                            vec3 pivot = vec3(-0.273, -1.25, 0.5);
                            
                            // Calculate rotation angle (swing right and back)
                            float angle = -sin(kick * 3.14159) * 0.5; // Swing 0.5 radians
                            
                            float s = sin(angle);
                            float c = cos(angle);
                            
                            // Translate to pivot
                            vec3 p = pos - pivot;
                            
                            // Rotate around Z
                            float xNew = p.x * c - p.y * s;
                            float yNew = p.x * s + p.y * c;
                            
                            // Translate back
                            pos.x = xNew + pivot.x;
                            pos.y = yNew + pivot.y;
                        }
                    }

                    // --- 3. Disintegration & Reformation (Type 1 & 2) ---
                    if (aType > 0.5) {
                        // Determine if this particle reacts
                        bool isCity = aType > 1.5;
                        bool isCapital = aType > 0.5 && aType < 1.5;
                        
                        // Only 20% of capital explodes (using random threshold)
                        // Cities always explode (to appear)
                        bool partOfBlast = isCity || (isCapital && aRandom.y > 0.8);

                        if (partOfBlast) {
                            // Calculate blast direction based on random seed
                            // Bias right (+x) and slightly up (+y)
                            vec3 blastDir = vec3(
                                0.5 + aRandom.x * 1.0,  // Rightward bias
                                0.2 + aRandom.y * 0.5,  // Upward bias
                                (aRandom.z - 0.5) * 0.5 // Slight Z spread
                            );
                            
                            // Explosion Physics
                            float explodeT = uExplode * 2.0; // Speed up explosion
                            vec3 blastPos = targetPos + blastDir * explodeT;
                            
                            // Add gravity arc
                            blastPos.y -= 0.5 * explodeT * explodeT; // Simple gravity
                            
                            // Reformation Logic
                            // We want to blend from blastPos back to targetPos based on uReform
                            float reform = smoothstep(0.0, 1.0, uReform);
                            
                            // Ease out reform
                            reform = 1.0 - pow(1.0 - reform, 3.0);
                            
                            // If exploding, override current pos
                            if (uExplode > 0.0) {
                                // If Type 2 (Cities), their "start" for explosion should be near "capital"
                                // They are currently at targetPos (bottom).
                                // Let's make them explode from a higher position (near capital y)
                                if (isCity && uReform < 0.1) {
                                    vec3 cityStart = targetPos;
                                    cityStart.y += 0.12; // Move up to capital level
                                    cityStart.x += (aRandom.x - 0.5) * 0.1; // Jitter
                                    
                                    // Recalculate blast for them from this new start
                                    vec3 cityBlast = cityStart + blastDir * explodeT;
                                    cityBlast.y -= 0.5 * explodeT * explodeT;
                                    
                                    pos = cityBlast;
                                } else {
                                    pos = blastPos;
                                }
                                
                                // Blend back to target
                                pos = mix(pos, targetPos, reform);
                            }
                        } else {
                            // Stable capital particles (80%)
                            // Add a small shake impact when kick hits (approx uKick > 0.4)
                            if (uKick > 0.4 && uKick < 0.8) {
                                float shake = (sin(uTime * 50.0) * 0.02) * (1.0 - uKick); // Decay
                                pos.x += shake;
                            }
                        }
                    }

                    // Jiggle effect (always active when formed)
                    // Reduce jiggle for cities (Type 2) for legibility
                    float baseJiggle = (aType > 1.5) ? 0.0005 : 0.002;
                    float jiggleStrength = baseJiggle * t; 
                    float speed = 3.0;
                    
                    pos.x += sin(uTime * speed + aRandom.x * 10.0) * jiggleStrength;
                    pos.y += cos(uTime * speed * 0.9 + aRandom.y * 10.0) * jiggleStrength;
                    pos.z += sin(uTime * speed * 1.1 + aRandom.z * 10.0) * jiggleStrength;

                    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                    gl_Position = projectionMatrix * mvPosition;
                    
                    vType = aType; // Pass type to fragment shader
                    
                    // Default point size
                    float pSize = 2.5 * (1.0 / -mvPosition.z);
                    
                    // Re-apply hiding logic for cities to be sure
                    if (aType > 1.5 && uExplode < 0.01 && uReform < 0.01) {
                        pSize = 0.0;
                    }
                    
                    gl_PointSize = pSize;
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                uniform float uDarkMode;
                varying float vType;
                
                void main() {
                    vec2 coord = gl_PointCoord - vec2(0.5);
                    if (length(coord) > 0.5) discard;
                    
                    // Colorize based on type
                    vec3 color;
                    if (vType < 0.5) {
                        // Type 0: Ampersand - London Red
                        color = vec3(0.808, 0.067, 0.149); // #CE1126
                    } else {
                        // Type 1 & 2: capital and cities - Respect dark mode
                        // Black in light mode, white in dark mode
                        color = mix(vec3(0.0, 0.0, 0.0), vec3(0.91, 0.91, 0.91), uDarkMode);
                    }
                    
                    gl_FragColor = vec4(color, 0.8); // Slight transparency
                }
            `,
            transparent: true,
            depthWrite: false
        });

        logoParticles = new THREE.Points(geometry, material);
        scene.add(logoParticles);
        console.log(`✓ Logo particles initialized with ${positionAttribute.count} particles`);
    }

    /**
     * Animation loop
     */
    function animate(currentTime) {
        animationId = requestAnimationFrame(animate);

        if (!scene || !camera || !renderer || !globe) return;

        if (startTime === 0) startTime = currentTime;
        const elapsed = (currentTime - startTime) / 1000;
        // Cap deltaTime to prevent huge jumps if tab is backgrounded
        const deltaTime = Math.min((currentTime - lastTime) / 1000, 0.1);
        lastTime = currentTime;

        // Update mouse 3D position from 2D screen coordinates
        if (mouse2D.x !== -999) {
            raycaster.setFromCamera(mouse2D, camera);
            const distance = camera.position.z - 0;
            mouse3D.copy(raycaster.ray.direction).multiplyScalar(distance).add(raycaster.ray.origin);
        }

        // Update globe uniforms
        let p = 0;
        if (globe.material.uniforms) {
            globe.material.uniforms.uTime.value = elapsed;
            const progressSpeed = 0.15; // Slower formation
            p = Math.min(1.0, elapsed * progressSpeed);
            p = 1 - Math.pow(1 - p, 3);
            globe.material.uniforms.uProgress.value = p;

            // Morph Logic
            const targetMorph = isExperienceActive ? 1.0 : 0.0;
            const morphSpeed = 1.0; // Speed of transition
            if (morphProgress < targetMorph) {
                morphProgress = Math.min(targetMorph, morphProgress + deltaTime * morphSpeed);
            } else if (morphProgress > targetMorph) {
                morphProgress = Math.max(targetMorph, morphProgress - deltaTime * morphSpeed);
            }
            globe.material.uniforms.uMorph.value = morphProgress;

            // Booth Morph Logic
            const targetBooth = isLetsTalkActive ? 1.0 : 0.0;
            const boothSpeed = 1.0;
            if (boothMorphProgress < targetBooth) {
                boothMorphProgress = Math.min(targetBooth, boothMorphProgress + deltaTime * boothSpeed);
            } else if (boothMorphProgress > targetBooth) {
                boothMorphProgress = Math.max(targetBooth, boothMorphProgress - deltaTime * boothSpeed);
            }
            globe.material.uniforms.uMorphBooth.value = boothMorphProgress;
        }

        // --- GPGPU UPDATE ---
        if (gpuCompute) {
            velocityUniforms.uTime.value = elapsed;
            velocityUniforms.uDeltaTime.value = deltaTime;
            velocityUniforms.uMouse.value.copy(mouse3D);

            positionUniforms.uTime.value = elapsed;
            positionUniforms.uDeltaTime.value = deltaTime;
        }

        // Update logo uniforms
        if (logoParticles && logoParticles.material.uniforms) {
            logoParticles.material.uniforms.uTime.value = elapsed;

            // Detect dark mode
            const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            logoParticles.material.uniforms.uDarkMode.value = isDarkMode ? 1.0 : 0.0;

            // 1. Formation (synchronized with globe)
            logoParticles.material.uniforms.uProgress.value = p;

            // 2. Sequence Kick, Explode, Reform
            // Only start if formation is complete (p >= 1.0)
            if (p >= 1.0) {
                const formationCompleteTime = 1.0 / 0.15;
                const timeSinceFormation = elapsed - formationCompleteTime;

                if (timeSinceFormation > 0) {
                    // Sequence Timing
                    const kickStart = 0.5;
                    const kickDuration = 0.5;

                    const explodeStart = kickStart + kickDuration; // 1.0s
                    const explodeDuration = 2.0;

                    const reformStart = explodeStart + 0.5; // 1.5s (overlap with explode)
                    const reformDuration = 1.5;

                    // Kick
                    let kickP = Math.max(0, Math.min(1, (timeSinceFormation - kickStart) / kickDuration));

                    // Explode (Disintegration)
                    let explodeP = Math.max(0, Math.min(1, (timeSinceFormation - explodeStart) / explodeDuration));

                    // Reform
                    let reformP = Math.max(0, Math.min(1, (timeSinceFormation - reformStart) / reformDuration));

                    // Ease out fall/reform
                    // fallP = 1 - Math.pow(1 - fallP, 3); // Removed old fall logic

                    logoParticles.material.uniforms.uKick.value = kickP;
                    logoParticles.material.uniforms.uExplode.value = explodeP;
                    logoParticles.material.uniforms.uReform.value = reformP;
                }
            }
        }

        gpuCompute.compute();

        if (fieldParticles) {
            fieldParticles.material.uniforms.texturePosition.value = gpuCompute.getCurrentRenderTarget(positionVariable).texture;
        }

        // Update city markers rotation to match globe
        if (cityMarkers) {
            // Sync Y rotation for spinning
            cityMarkers.rotation.y = globe.rotation.y;
            // Sync X rotation for tilt
            cityMarkers.rotation.x = globe.rotation.x;

            // Fade in markers based on formation progress
            // AND fade out when morphing to text OR booth
            let markerOpacity = Math.max(0, (p - 0.8) / 0.2); // Start at 0.8, full at 1.0

            // Fade out during text morph OR booth morph
            markerOpacity *= (1.0 - morphProgress) * (1.0 - boothMorphProgress);

            cityMarkers.children.forEach(marker => {
                marker.material.opacity = markerOpacity;
            });
        }

        // Globe rotation
        if (isZoomed) {
            // Smoothly interpolate to target rotation
            // Shortest path interpolation
            let current = globe.rotation.y;
            let target = targetGlobeRotationY;

            // Normalize angles
            while (target - current > Math.PI) target -= Math.PI * 2;
            while (target - current < -Math.PI) target += Math.PI * 2;

            globe.rotation.y += (target - current) * 0.05;
        } else {
            if (isExperienceActive) {
                // Force rotation to 0 (front) so text faces camera
                let currentY = globe.rotation.y;
                let targetY = 0;

                while (targetY - currentY > Math.PI) targetY -= Math.PI * 2;
                while (targetY - currentY < -Math.PI) targetY += Math.PI * 2;
                globe.rotation.y += (targetY - currentY) * 0.05;

                // Smoothly untilt to make text flat
                globe.rotation.x += (0 - globe.rotation.x) * 0.05;
            } else if (isLetsTalkActive) {
                // Booth Mode: Rotate continuously
                globe.rotation.y += baseRotationVelocity * deltaTime * 60;

                // Smoothly untilt (booth stands upright)
                globe.rotation.x += (0 - globe.rotation.x) * 0.05;
            } else {
                if (!isDragging) {
                    globe.rotation.y += baseRotationVelocity * deltaTime * 60;

                    // Restore tilt when not dragging and not in experience mode
                    const targetTilt = 22.5 * (Math.PI / 180);
                    globe.rotation.x += (targetTilt - globe.rotation.x) * 0.05;
                } else {
                    const diff = targetRotationY - globe.rotation.y;
                    globe.rotation.y += diff * 0.1 * (deltaTime * 60);
                }
            }
        }

        // Camera Zoom
        camera.position.z += (targetCameraZ - camera.position.z) * 0.05;

        renderer.render(scene, camera);
    }

    /**
     * Handle window resize
     */
    function onWindowResize() {
        if (!camera || !renderer) return;

        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    }

    // --- INTERACTION HANDLERS ---

    function onMouseDown(event) {
        // Calculate mouse position for raycasting
        const mouse = new THREE.Vector2();
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        if (!isDragging && !isZoomed) {
            // Check for city click
            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(cityMarkers.children);

            if (intersects.length > 0) {
                // Clicked a city
                const object = intersects[0].object;
                // Find the city data based on position (approximate match)
                // Or better, store data in userData
                // For now, let's just find the closest city to the clicked object
                let closestCity = null;
                let minDist = Infinity;

                officeLocations.forEach(loc => {
                    // Re-calculate pos to check
                    const phi = (90 - loc.lat) * (Math.PI / 180);
                    const theta = (loc.lon + 180) * (Math.PI / 180);
                    const radius = 1.06;
                    const x = -(radius * Math.sin(phi) * Math.cos(theta));
                    const z = (radius * Math.sin(phi) * Math.sin(theta));
                    const y = (radius * Math.cos(phi));

                    const dx = x - object.position.x;
                    const dy = y - object.position.y;
                    const dz = z - object.position.z;
                    const d = dx * dx + dy * dy + dz * dz;
                    if (d < minDist) {
                        minDist = d;
                        closestCity = loc;
                    }
                });

                if (closestCity) {
                    isZoomed = true;
                    focusCity = closestCity;
                    targetCameraZ = 1.8; // Zoom in

                    const lonRad = closestCity.lon * (Math.PI / 180);
                    targetGlobeRotationY = -lonRad - Math.PI / 2;

                    return;
                }
            }
        }

        // If we didn't click a city, handle drag or zoom out
        if (isZoomed) {
            // Clicked background -> Zoom out
            isZoomed = false;
            focusCity = null;
            targetCameraZ = 2.5;
            // Keep current rotation as target so it doesn't snap
            targetRotationY = globe.rotation.y;
        } else {
            // Start dragging
            isDragging = true;
            previousMousePosition = { x: event.clientX, y: event.clientY };
            // FIX: Sync target rotation with current rotation to prevent jumping
            if (globe) {
                targetRotationY = globe.rotation.y;
            }
        }
    }

    function onMouseMove(event) {
        // Update mouse position for particle avoidance (normalized device coordinates)
        mouse2D.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse2D.y = -(event.clientY / window.innerHeight) * 2 + 1;

        if (isDragging && !isZoomed) {
            const deltaMove = {
                x: event.clientX - previousMousePosition.x,
                y: event.clientY - previousMousePosition.y
            };
            targetRotationY += deltaMove.x * 0.005;
            previousMousePosition = { x: event.clientX, y: event.clientY };
        }
    }

    function onMouseUp() {
        isDragging = false;
    }

    function onTouchStart(event) {
        if (event.touches.length === 1) {
            isDragging = true;
            const touch = event.touches[0];
            previousMousePosition = { x: touch.clientX, y: touch.clientY };

            // FIX: Sync target rotation with current rotation to prevent jumping
            if (globe) {
                targetRotationY = globe.rotation.y;
            }

            // Update mouse position for particle avoidance
            mouse2D.x = (touch.clientX / window.innerWidth) * 2 - 1;
            mouse2D.y = -(touch.clientY / window.innerHeight) * 2 + 1;
        }
    }

    function onTouchMove(event) {
        if (event.touches.length === 1) {
            const touch = event.touches[0];

            // Update mouse position for particle avoidance
            mouse2D.x = (touch.clientX / window.innerWidth) * 2 - 1;
            mouse2D.y = -(touch.clientY / window.innerHeight) * 2 + 1;

            if (isDragging) {
                const deltaMove = {
                    x: touch.clientX - previousMousePosition.x,
                    y: touch.clientY - previousMousePosition.y
                };
                targetRotationY += deltaMove.x * 0.005;
                previousMousePosition = { x: touch.clientX, y: touch.clientY };
            }
        }
    }

    function onTouchEnd() {
        isDragging = false;
        // Reset mouse position when touch ends
        mouse2D.set(-999, -999);
    }

    function onMouseLeave() {
        // Reset mouse position when cursor leaves canvas
        mouse2D.set(-999, -999);
    }

    function setExperienceActive(active) {
        isExperienceActive = active;
    }

    function setLetsTalkActive(active) {
        isLetsTalkActive = active;
    }

    /**
     * Initialize globe
     */
    function init(container) {
        initScene();
        animate(0);
    }

    /**
     * Cleanup
     */
    function cleanup() {
        if (animationId) cancelAnimationFrame(animationId);
        if (renderer) renderer.dispose();
        if (globe) {
            globe.geometry.dispose();
            globe.material.dispose();
        }
        if (fieldParticles) {
            fieldParticles.geometry.dispose();
            fieldParticles.material.dispose();
        }
        if (cityMarkers) {
            cityMarkers.children.forEach(marker => {
                marker.geometry.dispose();
                marker.material.dispose();
            });
        }
        window.removeEventListener('resize', onWindowResize);
    }

    // Stubs for legacy compatibility
    function toggleFlows(visible) { }

    return {
        init,
        cleanup,
        toggleFlows,
        onWindowResize,
        onMouseDown,
        onMouseMove,
        onMouseUp,
        onMouseLeave,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
        selectedManager,
        setExperienceActive,
        setLetsTalkActive
    };
}
